import * as yup from 'yup'

interface DocumentValidationOptions {
  maxFiles?: number
  minFiles?: number
  required?: boolean
  allowedMimeTypes?: string[]
}
// TODO check function

export default function useValidations () {
  const { $t } = useI18n()

  const emailValidation = yup.string().matches(/[\w.-]+@[\w.-]+\.\w{2,}/, String($t('components.validations.invalidEmail'))).required(String($t('components.validations.emailRequired')))

  const passwordValidation = yup.string().required(String($t('components.validations.passwordRequired'))).min(8, String($t('components.validations.passwordMinLength')))

  const phoneValidation = yup.string().matches(/^(\+420|\+421)? ?[1-9][0-9]{2} ?[0-9]{3} ?[0-9]{3}$/, { message: String($t('components.validations.invalidPhone')), excludeEmptyString: true })

  // IČO
  const companyIdNumberValidation = yup.string().matches(/^\d{8}$/, String($t('components.validations.invalidCompanyId'))).required(String($t('components.validations.companyIdRequired')))
  // DIČ
  const companyTaxIdNumberValidation = yup.string()
    .nullable()
    .matches(/^(|[a-zA-Z]{2,4}[0-9]{8,12})$/, String($t('components.validations.invalidTaxId')))

  const debouncedCompanyNameCheck = useDebounceFn(async (name: string) => {
    const encodedName = encodeURIComponent(name)
    const { exists } = await $fetch<{ exists: boolean }>(`/api/exchange-offices/name/${encodedName}/exists`, {
      query: { name },
    })
    return exists
  }, 1000)

  // Company name validation with server-side check
  const exchangeOfficeNameValidation = yup
    .string()
    .required(String($t('components.form.companyNameRequired')))
    .min(6, String($t('components.form.companyNameMinLength')))
    .test('company-name-unique', String($t('components.form.companyNameExists')), async value => {
      if (!value || value.length < 6) return true // Skip validation if value is empty or too short

      try {
        const exists = await debouncedCompanyNameCheck(value)
        return !exists // Return false if company name exists (validation fails)
      }
      catch (_error) {
        // If API call fails, don't block the form submission
        return true
      }
    })

  // IBAN
  const ibanValidation = yup.string().uppercase().transform(value => value.replace(/\s+/g, ''))
    .matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/, String($t('components.validations.invalidIban')))
    .test('iban-check', String($t('components.validations.invalidIban')), value => {
      if (!value) return false
      try {
        // IBAN checksum algorithm: https://stackoverflow.com/questions/72689001/iban-validation-checksum
        const rearranged = value.slice(4) + value.slice(0, 4)
        const numericIban = rearranged.replace(/[A-Z]/g, match => String(match.charCodeAt(0) - 55))
        return BigInt(numericIban) % BigInt(97) === BigInt(1)
      }
      catch (_e) {
        return false
      }
    })

  // SWIFT/BIC
  const swiftBicValidation = yup.string().uppercase().transform(value => value.replace(/\s+/g, ''))
    .matches(/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/, String($t('components.validations.invalidSwiftBic')))

  function fileValidation (options?: DocumentValidationOptions) {
    const {
      maxFiles,
      minFiles,
      required = false,
      allowedMimeTypes = ['image/jpeg', 'image/png', 'application/pdf'],
    } = options || {}

    let schema = yup.array()

    if (required)
      schema = schema.test('required', String($t('components.validations.fileRequired')), files => !!files?.length)

    return schema
      .test('maxFiles', String($t('components.validations.maxFiles', { n: String(maxFiles) })), files => {
        if (maxFiles && files && files.length > maxFiles)
          return false

        return true
      })
      .test('minFiles', String($t('components.validations.minFiles', { n: String(minFiles) })), files => {
        if (minFiles && files && files.length < minFiles)
          return false

        return true
      })

      .of(
        yup
          .mixed<File>()
          .test('fileType', String($t('components.validations.unsupportedFileType')), file => {
            if (!file || !file.type)
              return false

            return allowedMimeTypes.includes(file.type)
          })
          .test('fileSize', String($t('components.validations.maxFileSizeExceeded')), file => {
            if (!file)
              return true

            const maxSizeInBytes = 4 * 1024 * 1024 // 4 MB
            return file.size <= maxSizeInBytes
          }),
      )
  }

  function confirmPasswordValidation (passwordFieldName: string) {
    return yup.string().required(String($t('components.validations.confirmPasswordRequired'))).oneOf([yup.ref(passwordFieldName)], String($t('components.validations.passwordsMustMatch')))
  }

  function passwordRequirements (input: string) {
    return [
      {
        text: String($t('components.validations.minCharacters')),
        valid: yup.string().min(8).required().isValidSync(input),
      },
      {
        text: String($t('components.validations.upperLowerCase')),
        valid: yup.string().matches(/[a-z]/).matches(/[A-Z]/).required().isValidSync(input),
      },
      {
        text: String($t('components.validations.oneNumber')),
        valid: yup.string().matches(/[0-9]/).required().isValidSync(input),
      },
      {
        text: String($t('components.validations.oneSpecialCharacter')),
        valid: yup.string().matches(/[!-/:-@[-`{-~]/).required().isValidSync(input),
      },
    ]
  }

  /**
   Validace na základě dokumentace z webu cssz: https://www.cssz.cz/standardni-kontrola-rodneho-cisla-a-evidencniho-cisla-pojistence
   */
  function czechPersonalIdNumberValidation (personalId: string) {
    // Remove spaces if any
    const cleanId = personalId.replace(/\s/g, '')

    // Check if it contains only numeric characters
    if (!/^\d+$/.test(cleanId)) {
      return false
    }

    // Check if length is either 9 or 10 digits
    if (cleanId.length !== 9 && cleanId.length !== 10) {
      return false
    }

    // Parse the personal ID into its components
    const year = parseInt(cleanId.substring(0, 2), 10)
    let month = parseInt(cleanId.substring(2, 4), 10)
    let day = parseInt(cleanId.substring(4, 6), 10)
    const extraNum = cleanId.substring(6)

    // Check if it's a 9-digit ID with "000" (invalid)
    if (cleanId.length === 9 && extraNum === '000') {
      return false
    }

    // Check for gender modifications
    const isWoman = month > 50
    if (isWoman) {
      month -= 50
    }

    // Check for special modifiers (RC+ and EČP)
    const isRCPlus = month > 20
    if (isRCPlus) {
      month -= 20
    }

    const isECP = day > 40
    if (isECP) {
      day -= 40
    }

    // RC+ and EČP cannot be both true
    if (isRCPlus && isECP) {
      return false
    }

    // Check month validity (1-12)
    if (month < 1 || month > 12) {
      return false
    }

    // Determine century and construct full year
    let fullYear
    if (cleanId.length === 10) {
      // For 10-digit IDs
      if (year > 53) {
        fullYear = 1900 + year
      }
      else {
        fullYear = 2000 + year
      }
    }
    else {
      // For 9-digit IDs
      fullYear = 1900 + year
    }

    // Check day validity based on month and year
    const daysInMonth = new Date(fullYear, month, 0).getDate()
    if (day < 1 || day > daysInMonth) {
      return false
    }

    // Check EČP constraints
    if (isECP) {
      if (cleanId.length === 9 && parseInt(extraNum, 10) < 600) {
        return false
      }
      if (cleanId.length === 10 && parseInt(extraNum, 10) < 6000) {
        return false
      }
    }

    // For 10-digit IDs, check divisibility by 11
    if (cleanId.length === 10) {
      return parseInt(cleanId, 10) % 11 === 0
    }

    // 9-digit IDs don't need the divisibility check
    return true
  }

  function cryptoAddressValidation (address: string, chain: string) {
    const cryptoAddresses = [
      { chain: 'Bitcoin', regex: /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/ },
      { chain: 'Ethereum', regex: /^0x[a-fA-F0-9]{40}$/ },
      { chain: 'Tron', regex: /^T[1-9A-HJ-NP-Za-km-z]{33}$/ },
      { chain: 'Monero', regex: /^[48][0-9A-Za-z]{94}$/ },
      { chain: 'Cardano', regex: /^addr1[0-9a-z]{98}$/ },
      { chain: 'Solana', regex: /^[1-9A-HJ-NP-Za-km-z]{44}$/ },
      { chain: 'Polkadot', regex: /^[1-9A-HJ-NP-Za-km-z]{47,48}$/ },
      { chain: 'BNB', regex: /^0x[a-fA-F0-9]{40}$/ },
      { chain: 'Arbitrum', regex: /^0x[a-fA-F0-9]{40}$/ },
      { chain: 'Polygon', regex: /^0x[a-fA-F0-9]{40}$/ },
      { chain: 'Ripple', regex: /^r[1-9A-HJ-NP-Za-km-z]{25,34}$/ },
      { chain: 'Litecoin', regex: /^(L|M)[a-km-zA-HJ-NP-Z1-9]{26,34}$/ },
      { chain: 'Stellar Lumens', regex: /^G[A-DH-NP-Z1-9]{55}$/ },
      { chain: 'Dogecoin', regex: /^D[1-9A-Za-z]{25,34}$/ },
      { chain: 'Polygon', regex: /^0x[a-fA-F0-9]{40}$/ },
      { chain: 'Avalanche', regex: /^0x[a-fA-F0-9]{40}$/ },
      { chain: 'Base', regex: /^0x[a-fA-F0-9]{40}$/ },
      { chain: 'Optimism', regex: /^0x[a-fA-F0-9]{40}$/ },
    ]
    const crypto = cryptoAddresses.find(crypto => crypto.chain === chain)
    if (!crypto)
      return false

    const regex = crypto.regex

    return regex.test(address)
  }
  return {
    emailValidation,
    passwordValidation,
    phoneValidation,
    companyIdNumberValidation,
    companyTaxIdNumberValidation,
    exchangeOfficeNameValidation,
    ibanValidation,
    swiftBicValidation,
    fileValidation,
    confirmPasswordValidation,
    passwordRequirements,
    cryptoAddressValidation,
    czechPersonalIdNumberValidation,
  }
}
